'use client';

import {ReactNode, useEffect, useState} from "react";
import Link from "next-intl/link";
import Image from "next/image";
import Logo from "@/img/logo.svg";
import LogoWhite from "@/img/logo_white.svg";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from 'next-intl/client';
import DesktopNavigation from "./navigation/desktop";
import MenuToggle from "./navigation/menutoggle";
import { useScope } from "@/app/context/scope-provider";
import {StrapiMenu} from "@/app/types/strapi.types";

export default function Header({ menu, children }: { menu: StrapiMenu, children: ReactNode }) {
    const { selectedScope } = useScope();
    const [scroll, setScroll] = useState(false);
    
    const [backgroundClass, setBackgroundClass] = useState("");
    const [textClass, setTextClass] = useState("");
    const [logo, setLogo] = useState(Logo)

    useEffect(() => {
        const listener = () => {
            setScroll(window.scrollY > 0);
        };
        window.addEventListener("scroll", listener);
        return () => {
            window.removeEventListener('scroll', listener);
        };
    }, []);

    const router = useRouter();
    const locale = useLocale();
    const pathname = usePathname();
    const firstPath = pathname.split("/").at(1);

    useEffect(() => {
        if (locale === "business" || (selectedScope === "business" && firstPath === "account") || (selectedScope === "therapist" && firstPath === "account")) {
            setBackgroundClass("bg-offblack");
            setTextClass("text-white");
            setLogo(LogoWhite);
        } else {
            setBackgroundClass("bg-white");
            setTextClass("");
            setLogo(Logo);
        }
    }, [locale, selectedScope, firstPath])

    const organisationJsonLdData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "id": "https://www.carecoins.co.uk",
        "logo": Logo.src,
        "legalName": "Carecoins",
        "name": "Carecoins",
        "contactPoint": [
            {
                "telephone": '+443000020061',
                "contactType": 'Support',
                "email": 'care@carecoins.co.uk',
                "areaServed": 'UK',
                "availableLanguage": ['English'],
            }
        ],
        "sameAs": ['https://www.facebook.com/CareCoinsUK', 'https://www.instagram.com/carecoinsuk/', 'https://www.linkedin.com/company/care-coins'],
        "url": "https://www.carecoins.co.uk"
    };

    const organisationJsonLdString = JSON.stringify(organisationJsonLdData);

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: organisationJsonLdString }}
            />

            {firstPath === "login" || firstPath === "forgot-password" || firstPath === "create-account" || firstPath === "create-password" || firstPath === "verify-account" || firstPath === "request-verify-email" ? null : (
                <>

                    <header
                        className={`py-4 lg:py-10 sticky top-0 z-50 transition-shadow ${scroll ? "drop-shadow-[0_15px_15px_rgba(0,0,0,0.05)]" : "drop-shadow-none"} ${backgroundClass}`}
                    >
                        <div className="container flex items-center justify-start lg:justify-between w-full">
                            {firstPath !== "account" && (<MenuToggle textClass={textClass} menu={menu} />)}
                            <div className="flex items-center">
                                <div className="max-w-[126px] lg:max-w-full">
                                    <Link href="/" title="Home">
                                        <Image src={logo} alt="Carecoins" />
                                    </Link>
                                </div>
                                {firstPath !== "account" && (
                                    <div className="hidden lg:flex ml-4">
                                        <a
                                            href={"/personal/" + pathname}
                                            className={`text-center text-[18px] tracking-wide py-2 px-1 mx-2 border-b ${locale === "personal" ? "border-primary" : "border-offblack text-white"} font-medium`}
                                        >
                                            <span className={`${locale === "personal" ? "" : "opacity-60"}`}>Personal</span>
                                        </a>
                                        <a
                                            href={"/business/" + pathname}
                                            className={`text-center text-[18px] tracking-wide py-2 px-1 mx-2 border-b ${locale === "business" ? "border-primary text-white" : "border-white"} font-medium`}
                                        >
                                            <span className={`${locale === "business" ? "" : "opacity-60"}`}>Business</span>
                                        </a>
                                    </div>
                                )}
                            </div>
                            <DesktopNavigation menu={menu} textClass={textClass} scope={selectedScope} />
                            {children}
                        </div>
                    </header>
                    {firstPath !== "account" && (
                        <div className={`lg:hidden flex ${locale === "business" ? "bg-offblack" : ""}`}>
                            <a
                                href={"/personal/" + pathname}
                                className={`w-1/2 text-center p-2 border-b-4 font-medium ${locale === "personal" ? "border-primary" : "border-offblack text-white"}`}
                            >
                                <span className={`${locale === "personal" ? "" : "opacity-60"}`}>Personal</span>
                            </a>
                            <a
                                href={"/business/" + pathname}
                                className={`w-1/2 text-center p-2 border-b-4 font-medium cursor-pointer ${locale === "business" ? "border-primary text-white" : "border-[#F9EBE2]"}`}
                            >
                                <span className={`${locale === "business" ? "" : "opacity-60"}`}>Business</span>
                            </a>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
